import React, { Children, useState, useEffect } from 'react'
import TransitionGroup from 'react-transition-group/TransitionGroup'
import CSSTransition from 'smartstream-widgets/controls/CSSTransition'
import Swipe from 'smartstream-widgets/controls/Swipe'
import { withComponentContext } from 'widgets-shared/core'
import { compose } from 'widgets-shared/core'
import CarouselArrow from './CarouselArrow/CarouselArrow'
import IndicatorDots from './IndicatorDots/IndicatorDots'

import { MessageBusType } from 'smartstream-widgets/types/sharedModule/core'
import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'


export type Slide = {
  index: number,
  isAdvert: boolean
}

type PrebidEventMsg = {
  body: {
    adUnitCodesInResponse: Array<string>
  }
}

type Props = {
  children: React.ReactElement,
  classes: {
    [K in string]: string
  },
  config: {
    monetization: {
      defaultAdStrategy: {
        advertSlideId: string,
        enableAutoScroll: false,
        advertsToShow: number,
        slidesBeforeAdvert: number,
        slideChangeMessage: string,
        renderAsyncMessage: string,
        type: string
      }
    },
    slidesBeforeAdvert: number,
    slidesToShow: number,
    enableAutoScroll?: boolean,
    usePrebid: boolean,
    advertisingStrategy: string
  },
  externalIndex: number,
  id: string,
  iconViewBox?: string,
  isContinuous?: boolean,
  leftControlText?: string,
  messageBus: MessageBusType,
  rightControlText?: string,
  setExternalIndex: ((arg0: number) => void),
  onSlideRender: ((slide: Slide) => void),
  showArrows: boolean,
  showIndicatorDots?: boolean,
  svgIconLinkHrefLeft: string,
  svgIconLinkHrefRight: string
}

const styles = (theme: Theme) =>  createStyles({
  activeIndicatorDot: {
    backgroundColor: theme.palette.primary.light
  },
  advertSlide: {
    background: theme.palette.common.black,
    position: 'relative',
    zIndex: 5
  },
  advertSlideDots: {},
  advertSlideButton: {},
  advertNavigationElements: {},
  carouselButton: {
    flex: '0 0 20%'
  },
  carouselButtonLeft: {},
  carouselButtonRight: {},
  carouselContainer: {},
  controlCaptions: {},
  controlIconsSvg: {},
  content: {
    height: '100%',
    overflow: 'hidden',
    position: 'relative'
  },
  enableAutoScroll: {
    zIndex: 5
  },
  indicatorDot: {},
  indicatorDots: {
    flex: 1
  },
  leftControlCaption: {},
  leftControlIconSvg: {},
  navigationElements: {
    display: 'flex',
    padding: `${theme.spacing(1)} 0px`,
    pointerEvents: 'none',
    backgroundColor: theme.palette.grey['50'],
    justifyContent: 'space-between',
    '&$advertNavigationElements': {
      display: 'none'
    },
    '&$enableAutoScroll': {
      display: 'flex'
    }
  },
  rightControlCaption: {},
  rightControlIconSvg: {},
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  swipeContainer: {
    flex: '1',
    height: '100%',
    minHeight: 0
  },
  transition_base: {
    height: '100%',
    position: 'absolute',
    transform: 'translateX(0)',
    transition: 'transform .25s ease-in-out',
    width: '100%'
  },
  transition_beforeEnter_left: {
    transform: 'translateX(-100%)'
  },
  transition_beforeEnter_right: {
    transform: 'translateX(100%)'
  },
  transition_exiting_left: {
    transform: 'translateX(100%)'
  },
  transition_exiting_right: {
    transform: 'translateX(-100%)'
  }
})

function Carousel (props: Props) {
  const {
    children,
    classes,
    config: {
      monetization: {
        defaultAdStrategy: {
          advertSlideId = 'carousel-advert-slide',
          enableAutoScroll = false,
          advertsToShow = 0,
          slidesBeforeAdvert: slidesBeforeAdvertFromConfig = 2,
          slideChangeMessage = 'slide',
          renderAsyncMessage = 'asyncadvert',
          type: strategyType
        } = {}
      } =  {},
      usePrebid = false,
      advertisingStrategy = false
    },
    externalIndex = 0,
    id = '',
    isContinuous = true,
    showArrows = true,
    iconViewBox,
    messageBus,
    onSlideRender,
    rightControlText,
    leftControlText,
    setExternalIndex,
    svgIconLinkHrefLeft,
    svgIconLinkHrefRight,
    showIndicatorDots = true
  } = props

  const childrenCount = Children.count(children)
  const isOnlyOneSlide = childrenCount === 1
  const shouldShowAds = !advertisingStrategy ? true : advertisingStrategy !== 'off'
  const slidesBeforeAdvert = isOnlyOneSlide ? 1 : slidesBeforeAdvertFromConfig
  const [currentSlide, setCurrentSlide] = useState({ index: 0, isAdvert: false })
  const [prebidState, setPrebidState] = useState({ ready: !usePrebid, requestSent: false})
  const [slideChange, setSlideChange] = useState({
    startedMoving: isOnlyOneSlide,
    advertsToShow: (shouldShowAds && strategyType === 'carousel' && advertsToShow ) || 0,
    directionToMove: 'left',
    newIndex: 0,
    slidesBeforeAd: slidesBeforeAdvert
  })
  const navCarouselAdvertClass = currentSlide.isAdvert ? classes.advertNavigationElements : ''
  const isLeftButtonActive = showArrows && (isOnlyOneSlide && slideChange.advertsToShow > 0 || isContinuous || currentSlide.index > 0)
  const isRightButtonActive = showArrows && (isOnlyOneSlide && slideChange.advertsToShow > 0 || isContinuous || currentSlide.index < childrenCount - 1)
  const indicatorDotsClasses = {
    root: classes.indicatorDots,
    dot: classes.indicatorDot,
    activeDot: classes.activeIndicatorDot,
    advertSlideDots: classes.advertSlideDots
  }
  const carouselButtonClasses = {
    carouselButton: classes.carouselButton,
    carouselButtonLeft: classes.carouselButtonLeft,
    carouselButtonRight: classes.carouselButtonRight,
    leftControlCaption: classes.leftControlCaption,
    leftControlIconSvg: classes.leftControlIconSvg,
    rightControlCaption: classes.rightControlCaption,
    rightControlIconSvg: classes.rightControlIconSvg,
    controlIconsSvg: classes.controlIconsSvg,
    controlCaptions: classes.controlCaptions,
    advertSlideButton: classes.advertSlideButton
  }

  useEffect(() => {
    function messageBusCallback () {
      const newIndex = slideChange.directionToMove === 'left'
        ? currentSlide.index - 1 : currentSlide.index + 1
      handleNavigation(newIndex)
    }

    messageBus && messageBus.addEventListener(renderAsyncMessage + ':removed', messageBusCallback)
    return () => {
      messageBus && messageBus.removeEventListener(renderAsyncMessage + ':removed', messageBusCallback)
    }
  }, [slideChange, currentSlide])

  useEffect(() => {
    const current = {
      index: slideChange.newIndex,
      isAdvert: prebidState.ready && slideChange.advertsToShow > 0 && slideChange.slidesBeforeAd <= 0
    }
    setCurrentSlide(current)
    if (onSlideRender) onSlideRender(current)
  }, [slideChange])

  useEffect(() => {
    if (!usePrebid) return

    function prebidReadyCallback(event: PrebidEventMsg) {
      const { body: { adUnitCodesInResponse = [] } = {} } = event
      setPrebidState({
        ready: adUnitCodesInResponse.includes('slot-container'),
        requestSent: false
      })
    }

    messageBus.addEventListener('prebid:ready', prebidReadyCallback)

    if (currentSlide.isAdvert) {
      setPrebidState({
        ready: false,
        requestSent: false
      })
    }

    if (slideChange.startedMoving && slideChange.advertsToShow > 0 &&
      !prebidState.ready && !prebidState.requestSent) {
      messageBus.dispatchEvent('prebid:request',{ adUnitCodes: ['slot-container'] })
      setPrebidState({
        ready: false,
        requestSent: true
      })
    }

    return () => {
      messageBus.removeEventListener('prebid:ready', prebidReadyCallback)
    }
  }, [currentSlide, usePrebid])

  useEffect(() => {
    if (currentSlide.isAdvert) messageBus && messageBus.dispatchEvent(renderAsyncMessage + ':render')
    if (setExternalIndex) setExternalIndex(currentSlide.index)
  }, [currentSlide])

  useEffect(() => {
    if (externalIndex !== currentSlide.index) handleNavigation(externalIndex)
  }, [externalIndex])

  useEffect(() => {
    const messageBusObserverCallback = (e: { body: { widgetWasInView: boolean } }) => {
      if (e.body.widgetWasInView) {
        handleNavigation(currentSlide.index + 1)
      }
    }
    messageBus && messageBus.addEventListener(slideChangeMessage + ':change', messageBusObserverCallback)

    return () => {
      messageBus && messageBus.removeEventListener(slideChangeMessage + ':change', messageBusObserverCallback)
    }
  }, [currentSlide])

  function handleNavigation (newIndex: number) {
    const direction = newIndex > currentSlide.index ? 'right' : 'left'
    if (newIndex === -1) newIndex = isContinuous ? childrenCount - 1 : 0
    if (newIndex >= childrenCount) newIndex = isContinuous ? 0 : childrenCount - 1
    setSlideChange({
      startedMoving: true,
      advertsToShow: currentSlide.isAdvert ? slideChange.advertsToShow - 1 : slideChange.advertsToShow,
      directionToMove: direction,
      newIndex: currentSlide.isAdvert ? currentSlide.index : newIndex,
      slidesBeforeAd: currentSlide.isAdvert ? slidesBeforeAdvert : slideChange.slidesBeforeAd - 1
    })
  }

  function getTransitionClasses () {
    const leftTransitionClasses = {
      base: classes.transition_base,
      beforeEnter: classes.transition_beforeEnter_left || classes.transition_beforeEnter,
      exiting: classes.transition_exiting_left || classes.transition_exiting
    }
    const rightTransitionClasses = {
      base: classes.transition_base,
      beforeEnter: classes.transition_beforeEnter_right || classes.transition_beforeEnter,
      exiting: classes.transition_exiting_right || classes.transition_exiting
    }
    return slideChange.directionToMove === 'left' ? leftTransitionClasses : rightTransitionClasses
  }

  return (
    <div className={`${classes.carouselContainer} ${classes.root}`} id={id} data-testid={'carousel-container'}>
      <Swipe
        className={classes.swipeContainer}
        leftSwipeCallback={() => { handleNavigation(currentSlide.index + 1) }}
        rightSwipeCallback={() => { handleNavigation(currentSlide.index - 1) }}
      >
        <TransitionGroup className={classes.content}>
          <CSSTransition
            key={currentSlide.isAdvert ? advertSlideId : currentSlide.index}
            transitionClasses={getTransitionClasses()}
          >
            {currentSlide.isAdvert ?
              <div id={advertSlideId} className={classes.advertSlide} />
              : Children.toArray(children)[currentSlide.index]
            }
          </CSSTransition>
        </TransitionGroup>
      </Swipe>
      {(showArrows || showIndicatorDots) &&
      <nav className={`${classes.navigationElements} ${navCarouselAdvertClass} ${enableAutoScroll ? classes.enableAutoScroll : ''}`}>
        {
          showArrows &&
          <CarouselArrow
            enableAutoScroll={enableAutoScroll}
            classes={carouselButtonClasses}
            controlText={leftControlText}
            currentIndex={currentSlide.index}
            onClick={handleNavigation}
            iconViewBox={iconViewBox}
            isAdvertShowing={currentSlide.isAdvert}
            isButtonActive={isLeftButtonActive}
            isPrevious
            svgIconLinkHref={svgIconLinkHrefLeft}
          />
        }
        {
          showIndicatorDots &&
          <IndicatorDots
            classes={indicatorDotsClasses}
            currentIndex={currentSlide.index}
            isAdvertShowing={currentSlide.isAdvert}
            numberOfDots={Children.count(children)}
            onClick={handleNavigation}
          />
        }
        {
          showArrows &&
          <CarouselArrow
            enableAutoScroll={enableAutoScroll}
            classes={carouselButtonClasses}
            controlText={rightControlText}
            currentIndex={currentSlide.index}
            iconViewBox={iconViewBox}
            isButtonActive={isRightButtonActive}
            isAdvertShowing={currentSlide.isAdvert}
            onClick={handleNavigation}
            svgIconLinkHref={svgIconLinkHrefRight}
          />
        }
      </nav>
      }
    </div>
  )
}

export default compose(
  withComponentContext,
  withStyles(styles, { name: 'Carousel', defaultTheme: getDefaultTheme })
)(Carousel)
